import { makeObservable, observable, action } from 'mobx';
import React from 'react';
import { GenderStore } from './genderStore';
import { EnduranceStore } from './enduranceStore';
import { FitnessLevelStore } from './fitnessLevelStore';
import { PersonalBestStore } from './personalBestStore';
import { RunningHistoryStore } from './runningHistoryStore';
import { WalkingTimeStore } from './walkingTimeStore';
import { GoalsStore } from './goalsStore';
import { MotivationStore } from './motivationStore';
import { MusicStore } from './musicStore';
import { PageType, pathForPage } from '../utils/pageType';

export class RootStore {
  genderStore: GenderStore;

  enduranceStore: EnduranceStore;

  fitnessLevelStore: FitnessLevelStore;

  personalBestStore: PersonalBestStore;

  runningHistoryStore: RunningHistoryStore;

  walkingTimeStore: WalkingTimeStore;

  goalStore: GoalsStore;

  motivationStore: MotivationStore;

  musicStore: MusicStore;

  pages: PageType[];

  lastKnowPageIndex: number;

  constructor() {
    this.genderStore = new GenderStore();
    this.enduranceStore = new EnduranceStore();
    this.fitnessLevelStore = new FitnessLevelStore();
    this.personalBestStore = new PersonalBestStore();
    this.runningHistoryStore = new RunningHistoryStore();
    this.walkingTimeStore = new WalkingTimeStore();
    this.goalStore = new GoalsStore();
    this.motivationStore = new MotivationStore();
    this.musicStore = new MusicStore();
    this.pages = [
      'GetStarted',
      'CollectGender',
      'CollectGoals',
      'CollectMotivations',
      'CollectFitnessLevel',
      'CollectRunningHistory',
      'CollectWalkingTime',
      'CollectEndurance',
      'CollectPersonalBest',
      'CollectMusic',
      'BuildingProfile',
      'SendToApp',
    ];
    this.lastKnowPageIndex = 0;

    makeObservable(this, {
      genderStore: observable,
      enduranceStore: observable,
      fitnessLevelStore: observable,
      personalBestStore: observable,
      runningHistoryStore: observable,
      walkingTimeStore: observable,
      goalStore: observable,
      motivationStore: observable,
      musicStore: observable,
      pages: observable,
      lastKnowPageIndex: observable,
      getNextPagePath: action,
      setLastKnowPageIndex: action,
    });
  }

  setLastKnowPageIndex = (index: number) => {
    this.lastKnowPageIndex = index;
  };

  getNextPagePath = (previousPage: PageType) => {
    for (let i = 0; i < this.pages.length - 1; i += 1) {
      const page = this.pages[i];
      if (page === previousPage) {
        const nextPage = this.pages[i + 1];
        return pathForPage(nextPage);
      }
    }
    return pathForPage('GetStarted');
  };

  createDeepLink = () => {
    const pushLink = new URL('pushtraining://pushtrainingapp.com/start');

    if (this.genderStore.selectedGender) {
      pushLink.searchParams.append('gender', this.genderStore.selectedGender);
    }

    if (this.goalStore.selectedGoals && this.goalStore.selectedGoals.size > 0) {
      const goalsArr = Array.from(this.goalStore.selectedGoals);
      pushLink.searchParams.append('goals', goalsArr.join(','));
    }

    if (
      this.motivationStore.selectedMotivations &&
      this.motivationStore.selectedMotivations.size > 0
    ) {
      const motivationsArr = Array.from(
        this.motivationStore.selectedMotivations,
      );
      pushLink.searchParams.append('motivations', motivationsArr.join(','));
    }
    if (this.fitnessLevelStore.selectedFitnessLevel) {
      pushLink.searchParams.append(
        'fitnessLevel',
        this.fitnessLevelStore.selectedFitnessLevel,
      );
    }
    if (this.runningHistoryStore.selectedRunningHistory) {
      pushLink.searchParams.append(
        'runningHistory',
        this.runningHistoryStore.selectedRunningHistory,
      );
    }
    if (this.walkingTimeStore.selectedWalkingTime) {
      pushLink.searchParams.append(
        'walkingTime',
        this.walkingTimeStore.selectedWalkingTime,
      );
    }
    if (this.enduranceStore.selectedEndurance) {
      pushLink.searchParams.append(
        'endurance',
        this.enduranceStore.selectedEndurance,
      );
    }
    if (this.personalBestStore.personalBestInTenths) {
      pushLink.searchParams.append(
        'personalBest',
        this.personalBestStore.personalBestInTenths.toString(),
      );
    }

    if (
      this.musicStore.selelctedMusic &&
      this.musicStore.selelctedMusic.size > 0
    ) {
      const musicArr = Array.from(this.musicStore.selelctedMusic);
      pushLink.searchParams.append('music', musicArr.join(','));
    }

    pushLink.searchParams.append('discoveryTitle', 'webOnboarding');

    return pushLink.href;
  };
}
const StoresContext = React.createContext(new RootStore());
export const useStores = () => React.useContext(StoresContext);
